import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Download, Dna, ArrowLeft, ShoppingCart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EXAMPLE_SEQUENCE = `>part of X00351 Human mRNA for beta-actin
CACGGCATCGTCACCAACTGGGACGACATGGAGAAAATCTGGCACCACACCTTCTACAATGAGCTGCGTGTGGCTCCCGA
GGAGCACCCCGTGCTGCTGACCGAGGCCCCCCTGAACCCCAAGGCCAACCGCGAGAAGATGACCCAGATCATGTTTGAGA
CCTTCAACACCCCAGCCATGTACGTTGCTATCCAGGCTGTGCTATCCCTGTACGCCTCTGGCCGTACCACTGGCATCGTG
ATGGACTCCGGTGACGGGGTCACCCACACTGTGCCCATCTACGAGGGGTATGCCCTCCCC`;

function ProbeReadyDesign() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart, getItemCount } = useCart();
  const [sequence, setSequence] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedAssays, setSelectedAssays] = useState({});
  const [assayVolumes, setAssayVolumes] = useState({}); // Track volume for each assay
  const fileInputRef = useRef(null);
  
  const [params, setParams] = useState({
    primer_opt_size: 20,
    primer_min_size: 18,
    primer_max_size: 25,
    amplicon_min_size: 70,
    amplicon_max_size: 150,
    primer_opt_tm: 60.0,
    primer_min_tm: 57.0,
    primer_max_tm: 63.0,
    primer_min_gc: 40.0,
    primer_max_gc_clamp: 2,
    primer_max_self_any: 8.0,
    primer_max_self_end: 3.0
  });

  const highlightProbeInAmplicon = (amplicon, probeSeq, probePos, leftPrimerPos) => {
    const leftPrimerStart = parseInt(leftPrimerPos.split('-')[0]);
    const relativeProbePos = probePos - leftPrimerStart;
    
    if (relativeProbePos < 0 || relativeProbePos >= amplicon.length) {
      return amplicon;
    }
    
    const before = amplicon.substring(0, relativeProbePos);
    const probe = amplicon.substring(relativeProbePos, relativeProbePos + probeSeq.length);
    const after = amplicon.substring(relativeProbePos + probeSeq.length);
    
    return (
      <>
        {before}
        <span className="bg-yellow-200 font-semibold">{probe}</span>
        {after}
      </>
    );
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setSelectedFile(file);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await axios.post(`${API}/upload`, formData);
      if (response.data.success) {
        const seqs = response.data.sequences.map(s => `>${s.name}\n${s.sequence}`).join('\n\n');
        setSequence(seqs);
        toast.success(`Loaded ${response.data.count} sequence(s)`);
      }
    } catch (error) {
      toast.error("Failed to upload file");
      console.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!sequence.trim()) {
      toast.error("Please enter a sequence");
      return;
    }
    
    setLoading(true);
    setResults(null);
    setSelectedAssays({});
    
    try {
      const response = await axios.post(`${API}/design`, {
        sequence,
        ...params
      });
      
      setResults(response.data);
      
      const totalAssays = response.data.reduce((sum, r) => sum + r.assays.length, 0);
      
      if (totalAssays > 0) {
        toast.success(`Found ${totalAssays} assay design(s)`);
      } else {
        toast.warning("No suitable assays found. Try adjusting parameters.");
      }
    } catch (error) {
      toast.error("Design failed: " + (error.response?.data?.detail || error.message));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!results) return;
    
    try {
      const response = await axios.post(`${API}/export`, results, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'pra_design_results.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Results exported successfully");
    } catch (error) {
      toast.error("Export failed");
      console.error(error);
    }
  };

  const loadExample = () => {
    setSequence(EXAMPLE_SEQUENCE);
    toast.info("Example sequence loaded");
  };

  const toggleAssaySelection = (seqIdx, assayIdx) => {
    const key = `${seqIdx}-${assayIdx}`;
    setSelectedAssays(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const addToBasket = async (seqIdx, assayIdx) => {
    const assay = results[seqIdx].assays[assayIdx];
    const sequenceName = results[seqIdx].sequence_name;
    const key = `${seqIdx}-${assayIdx}`;
    const volume = assayVolumes[key] || '100rxn';
    const price = volume === '500rxn' ? 550 : 300;
    
    try {
      await addToCart({
        technology: 'probe_ready_assay',
        application: 'upl_probe_design',
        configuration: {
          sequence_name: sequenceName,
          probe_id: assay.probe_id,
          probe_sequence: assay.probe_sequence,
          left_primer: assay.left_primer,
          right_primer: assay.right_primer,
          amplicon_length: assay.amplicon_length,
          left_primer_tm: assay.left_primer_tm,
          right_primer_tm: assay.right_primer_tm,
          assay_number: assayIdx + 1,
          volume: volume
        },
        price: price,
        quantity: 1
      });
      toast.success(`Added ${sequenceName} - Assay #${assayIdx + 1} (${volume}, €${price}) to cart!`);
    } catch (error) {
      toast.error('Failed to add to cart');
      console.error(error);
    }
  };

  const addAllToCart = async () => {
    const selectedCount = Object.values(selectedAssays).filter(Boolean).length;
    if (selectedCount === 0) {
      toast.error("Please select at least one assay");
      return;
    }
    
    try {
      let addedCount = 0;
      for (const key in selectedAssays) {
        if (selectedAssays[key]) {
          const [seqIdx, assayIdx] = key.split('-').map(Number);
          const assay = results[seqIdx].assays[assayIdx];
          const sequenceName = results[seqIdx].sequence_name;
          
          await addToCart({
            technology: 'probe_ready_assay',
            application: 'upl_probe_design',
            configuration: {
              sequence_name: sequenceName,
              probe_id: assay.probe_id,
              probe_sequence: assay.probe_sequence,
              left_primer: assay.left_primer,
              right_primer: assay.right_primer,
              amplicon_length: assay.amplicon_length,
              left_primer_tm: assay.left_primer_tm,
              right_primer_tm: assay.right_primer_tm,
              assay_number: assayIdx + 1
            },
            price: 300,
            quantity: 1
          });
          addedCount++;
        }
      }
      toast.success(`Added ${addedCount} assay(s) to cart!`);
      // Clear selections after adding
      setSelectedAssays({});
    } catch (error) {
      toast.error('Failed to add items to cart');
      console.error(error);
    }
  };

  const selectedCount = Object.values(selectedAssays).filter(Boolean).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <Dna className="w-6 h-6 text-blue-600" />
              <span className="font-semibold text-lg">Probe Ready Assay Design</span>
            </div>
            <div className="flex items-center space-x-4">
              <Button 
                variant="outline" 
                onClick={() => navigate('/workflow')}
                data-testid="change-application-button"
              >
                Change Application
              </Button>
              {user && (
                <Button onClick={() => navigate('/cart')} variant="outline" className="relative">
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  Cart
                  {getItemCount() > 0 && (
                    <span className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {getItemCount()}
                    </span>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          <Card data-testid="input-card">
            <CardHeader>
              <CardTitle className="text-xl font-semibold">Enter DNA Sequence</CardTitle>
              <CardDescription>Paste your FASTA sequence or upload a file. The tool will find READY probes and design primers around them.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <Textarea
                    data-testid="sequence-input"
                    placeholder=">Sequence name\nATGCGTACGTACGTACGT...\n\nOr paste multiple FASTA sequences"
                    value={sequence}
                    onChange={(e) => setSequence(e.target.value)}
                    rows={8}
                    className="font-mono text-sm"
                  />
                  
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="upload-button"
                    >
                      <Upload className="w-5 h-5 mr-2" />
                      {selectedFile ? selectedFile.name : "Upload FASTA"}
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".fasta,.fa,.txt"
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                    />
                    
                    <Button
                      type="button"
                      variant="outline"
                      onClick={loadExample}
                      data-testid="example-button"
                    >
                      Load Example
                    </Button>
                  </div>
                </div>

                <Accordion type="single" collapsible className="params-accordion">
                  <AccordionItem value="params">
                    <AccordionTrigger data-testid="params-toggle">Optional Parameters (defaults recommended)</AccordionTrigger>
                    <AccordionContent>
                      <div className="params-grid">
                        <div className="param-group">
                          <Label className="param-label">Primer Length (nt)</Label>
                          <div className="param-inputs">
                            <div>
                              <Label className="sub-label">Optimum</Label>
                              <Input
                                type="number"
                                value={params.primer_opt_size}
                                onChange={(e) => setParams({...params, primer_opt_size: parseInt(e.target.value)})}
                                data-testid="primer-opt-size"
                              />
                            </div>
                            <div>
                              <Label className="sub-label">Min</Label>
                              <Input
                                type="number"
                                value={params.primer_min_size}
                                onChange={(e) => setParams({...params, primer_min_size: parseInt(e.target.value)})}
                                data-testid="primer-min-size"
                              />
                            </div>
                            <div>
                              <Label className="sub-label">Max</Label>
                              <Input
                                type="number"
                                value={params.primer_max_size}
                                onChange={(e) => setParams({...params, primer_max_size: parseInt(e.target.value)})}
                                data-testid="primer-max-size"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="param-group">
                          <Label className="param-label">Amplicon Length (nt)</Label>
                          <div className="param-inputs">
                            <div>
                              <Label className="sub-label">Min</Label>
                              <Input
                                type="number"
                                value={params.amplicon_min_size}
                                onChange={(e) => setParams({...params, amplicon_min_size: parseInt(e.target.value)})}
                                data-testid="amplicon-min-size"
                              />
                            </div>
                            <div>
                              <Label className="sub-label">Max</Label>
                              <Input
                                type="number"
                                value={params.amplicon_max_size}
                                onChange={(e) => setParams({...params, amplicon_max_size: parseInt(e.target.value)})}
                                data-testid="amplicon-max-size"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="param-group">
                          <Label className="param-label">Melting Temperature (°C)</Label>
                          <div className="param-inputs">
                            <div>
                              <Label className="sub-label">Optimum</Label>
                              <Input
                                type="number"
                                step="0.1"
                                value={params.primer_opt_tm}
                                onChange={(e) => setParams({...params, primer_opt_tm: parseFloat(e.target.value)})}
                                data-testid="primer-opt-tm"
                              />
                            </div>
                            <div>
                              <Label className="sub-label">Min</Label>
                              <Input
                                type="number"
                                step="0.1"
                                value={params.primer_min_tm}
                                onChange={(e) => setParams({...params, primer_min_tm: parseFloat(e.target.value)})}
                                data-testid="primer-min-tm"
                              />
                            </div>
                            <div>
                              <Label className="sub-label">Max</Label>
                              <Input
                                type="number"
                                step="0.1"
                                value={params.primer_max_tm}
                                onChange={(e) => setParams({...params, primer_max_tm: parseFloat(e.target.value)})}
                                data-testid="primer-max-tm"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="param-group">
                          <Label className="param-label">Advanced Properties</Label>
                          <div className="param-inputs">
                            <div>
                              <Label className="sub-label">GC Min %</Label>
                              <Input
                                type="number"
                                value={params.primer_min_gc}
                                onChange={(e) => setParams({...params, primer_min_gc: parseFloat(e.target.value)})}
                                data-testid="primer-min-gc"
                              />
                            </div>
                            <div>
                              <Label className="sub-label">GC Clamp</Label>
                              <Input
                                type="number"
                                value={params.primer_max_gc_clamp}
                                onChange={(e) => setParams({...params, primer_max_gc_clamp: parseInt(e.target.value)})}
                                data-testid="primer-max-gc-clamp"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

                <Button
                  type="submit"
                  disabled={loading}
                  className="submit-button"
                  data-testid="submit-button"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Designing Assays...
                    </>
                  ) : (
                    <>
                      <Dna className="w-5 h-5 mr-2" />
                      Design Assays
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {results && (
            <div className="results-section" data-testid="results-section">
              <div className="results-header">
                <h2 className="results-title">Assay Design Results</h2>
                <Button
                  onClick={handleExport}
                  variant="outline"
                  data-testid="export-button"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Export CSV
                </Button>
              </div>

              {results.map((result, idx) => (
                <Card key={idx} className="result-card" data-testid={`result-card-${idx}`}>
                  <CardHeader>
                    <CardTitle className="result-seq-title">{result.sequence_name}</CardTitle>
                    <CardDescription>
                      Length: {result.sequence_length} bp
                      {result.success ? 
                        ` | ${result.assays.length} assay design(s) found` :
                        ''
                      }
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {result.success && result.assays.length > 0 ? (
                      <div className="assays-container">
                        {result.assays.map((assay, aidx) => (
                          <div key={aidx} className="bg-white border border-gray-200 rounded-lg p-6 mb-4" data-testid={`assay-${aidx}`}>
                            <div className="flex justify-between items-center mb-4">
                              <div className="flex items-center space-x-3">
                                <Checkbox
                                  checked={selectedAssays[`${idx}-${aidx}`] || false}
                                  onCheckedChange={() => toggleAssaySelection(idx, aidx)}
                                  data-testid={`assay-checkbox-${idx}-${aidx}`}
                                />
                                <div>
                                  <h3 className="text-lg font-semibold text-gray-900">Assay #{aidx + 1}</h3>
                                  <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded">READY Probe #{assay.probe_id}</span>
                                </div>
                              </div>
                              <div className="flex items-center space-x-3">
                                <div className="flex flex-col">
                                  <Label htmlFor={`volume-${idx}-${aidx}`} className="text-sm text-gray-600 mb-1">Volume</Label>
                                  <Select 
                                    value={assayVolumes[`${idx}-${aidx}`] || '100rxn'} 
                                    onValueChange={(val) => setAssayVolumes({...assayVolumes, [`${idx}-${aidx}`]: val})}
                                  >
                                    <SelectTrigger className="w-32" id={`volume-${idx}-${aidx}`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="100rxn">100 rxn (€300)</SelectItem>
                                      <SelectItem value="500rxn">500 rxn (€550)</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <Button
                                  size="sm"
                                  disabled={!selectedAssays[`${idx}-${aidx}`]}
                                  onClick={() => addToBasket(idx, aidx)}
                                  data-testid={`add-to-basket-${idx}-${aidx}`}
                                  className="mt-6"
                                >
                                  <ShoppingCart className="w-4 h-4 mr-2" />
                                  Add to Basket
                                </Button>
                              </div>
                            </div>
                            
                            <div className="overflow-x-auto">
                              <table className="w-full border-collapse">
                                <thead>
                                  <tr>
                                    <th>Primer</th>
                                    <th>Length</th>
                                    <th>Position</th>
                                    <th>Tm (°C)</th>
                                    <th>GC %</th>
                                    <th>Sequence (5' → 3')</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr data-testid={`assay-${aidx}-left`}>
                                    <td className="primer-label">Left</td>
                                    <td>{assay.left_primer_length}</td>
                                    <td>{assay.left_primer_position}</td>
                                    <td>{assay.left_primer_tm.toFixed(1)}</td>
                                    <td>{assay.left_primer_gc.toFixed(1)}</td>
                                    <td className="sequence-cell">{assay.left_primer}</td>
                                  </tr>
                                  <tr data-testid={`assay-${aidx}-right`}>
                                    <td className="primer-label">Right</td>
                                    <td>{assay.right_primer_length}</td>
                                    <td>{assay.right_primer_position}</td>
                                    <td>{assay.right_primer_tm.toFixed(1)}</td>
                                    <td>{assay.right_primer_gc.toFixed(1)}</td>
                                    <td className="sequence-cell">{assay.right_primer}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                            
                            <div className="amplicon-info">
                              <div className="amplicon-header">
                                <span className="amplicon-label">Amplicon ({assay.amplicon_length} nt)</span>
                                <span className="probe-info">Probe at position {assay.probe_position} ({assay.probe_strand})</span>
                              </div>
                              <div className="amplicon-sequence">
                                {highlightProbeInAmplicon(
                                  assay.amplicon_sequence, 
                                  assay.probe_sequence, 
                                  assay.probe_position,
                                  assay.left_primer_position
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="no-assays" data-testid="no-assays">
                        <h3 className="no-assays-title">Sorry, no potential assays were found for this sequence</h3>
                        <p className="no-assays-text">Perhaps try a different region or longer sequence?</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}

              {selectedCount > 1 && (
                <div className="mt-6 flex justify-center">
                  <Button
                    size="lg"
                    onClick={addAllToCart}
                    data-testid="add-all-cart-button"
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    Add All to Cart ({selectedCount})
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default ProbeReadyDesign;